import { convertWarekiWrap } from '@/utils/wareki';
import type { RezeptListItem } from '@/types/rezept.types';

/** Full-width → half-width digits. */
export function toHalfWidthDigits(input: string): string {
  return input.replace(/[０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xfee0));
}

/** Half-width + half-width kana + half-width punctuation (subset). */
export function toHalfWidthAnk(input: string): string {
  return input.replace(/[Ａ-Ｚａ-ｚ０-９]/g, ch =>
    String.fromCharCode(ch.charCodeAt(0) - 0xfee0)
  );
}

/**
 * Convert stored yearmonth "YYYYMM" to wareki display string "令和06年04月".
 * Returns input untouched if it doesn't match.
 */
export function formatTreatementYearmonth(yyyymm: string | undefined | null): string {
  if (!yyyymm || !/^\d{6}$/.test(yyyymm)) return yyyymm ?? '';
  const y = Number(yyyymm.slice(0, 4));
  const m = Number(yyyymm.slice(4, 6));
  const seireki = `${y}/${String(m).padStart(2, '0')}/01`;
  const wareki = convertWarekiWrap(seireki);
  // convertWarekiWrap returns "令和06年04月01日" — trim the day portion for month display.
  return wareki.replace(/\d{2}日$/, '').replace(/\d{2}日/, '');
}

/** Month diff between "YYYYMM" strings (b - a), positive when b is later. */
export function diffInMonths(aYyyymm: string, bYyyymm: string): number {
  if (!/^\d{6}$/.test(aYyyymm) || !/^\d{6}$/.test(bYyyymm)) return 0;
  const ay = Number(aYyyymm.slice(0, 4));
  const am = Number(aYyyymm.slice(4, 6));
  const by = Number(bYyyymm.slice(0, 4));
  const bm = Number(bYyyymm.slice(4, 6));
  return (by - ay) * 12 + (bm - am);
}

/**
 * Determine the 遅/返 badge label for a rezept row.
 * Mirrors RA2 blade logic:
 *  - is_return===1 → 「返」
 *  - else if treatment→rezept month span > 1 → 「遅」
 *  - else empty.
 */
export function formatLateReturnBadge(row: RezeptListItem): string {
  if (row.is_return === 1) return '返';
  if (diffInMonths(row.treatement_yearmonth, row.rezept_yearmonth) > 1) return '遅';
  return '';
}

export function formatRezeptKbn(kbn: number | undefined, nonoutputRezeptMedicalAidNumber?: 0 | 1): string {
  if (kbn === 2) return '関';
  return nonoutputRezeptMedicalAidNumber ? '本+関' : '本';
}

export function formatStatus(status: number | undefined): string {
  if (status === 1) return '印刷済';
  if (status === 2) return '確定';
  return '';
}

export function hasCreatedTreatmentReport(row: RezeptListItem): boolean {
  return (
    (!!row.is_treatment_report && !!row.treatment_report_id) ||
    (!!row.second_report && !!row.second_report_id)
  );
}

export function hasCheckedTreatmentReportFee(row: RezeptListItem): boolean {
  return row.add_treatment_report === 1;
}

export function getDuplicateTreatmentReportFeeIds(rows: RezeptListItem[]): Set<number> {
  const idsByPatientAndSubmissionMonth = new Map<string, number[]>();

  rows.forEach(row => {
    if (
      !row.rezept_yearmonth ||
      !hasCreatedTreatmentReport(row) ||
      !hasCheckedTreatmentReportFee(row)
    ) {
      return;
    }

    const key = `${row.patient_id}:${row.rezept_yearmonth}`;
    idsByPatientAndSubmissionMonth.set(key, [
      ...(idsByPatientAndSubmissionMonth.get(key) ?? []),
      row.id,
    ]);
  });

  const duplicateIds = new Set<number>();
  idsByPatientAndSubmissionMonth.forEach(ids => {
    if (ids.length < 2) return;
    ids.forEach(id => duplicateIds.add(id));
  });

  return duplicateIds;
}
